/**
 * @Page WareResi
 * @Description 창고등록 > 창고별 상주원 연계 팝업
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import Grid from "@/components/grid/agGrid";
import CustomAlert from "@/components/molecules/CustomAlert";
import * as Common from "@/utils/Common";

const SERVER_URL = process.env.REACT_APP_BACKEND_SERVER;

function WareResi({ onClose, wrcd, vnt1 }) {
    const token = Common.getToken();
    const grdWareResi = useRef();
    const latestExistingSet = useRef(new Set());
    const pendingPreSelect = useRef(false);
    const [searchApi, setSearchApi] = useState(false);
    const [message, setMessage] = useState({ openAlert: false, severity: "success", msg: "" });
    const { openAlert, severity, msg } = message;

    const vendor = vnt1 || Common.getToken("vand");

    const responseMessage = (data, fallback) =>
        data?.errors?.message || data?.error || data?.message || fallback;

    const loadExistingResidents = async () => {
        const para = `{"vnt1":"${vendor}","wrcd":"${wrcd}"}`;
        const query = SERVER_URL + "/rst01/wareresi/search?query=" + encodeURI(para);
        let result = new Set();
        try {
            const data = await fetch(query, { headers: { Authorization: token } }).then(r => r.json());
            if (data.success && data.data) {
                result = new Set(data.data.filter(row => row.useyn === "Y").map(row => row.recd).filter(Boolean));
            }
        } catch (e) {
            // 조회 실패는 그리드 조회 결과로 안내한다.
        }
        latestExistingSet.current = result;
        return result;
    };

    const onClearApi = useCallback(() => {
        setSearchApi(false);
        if (pendingPreSelect.current) {
            pendingPreSelect.current = false;
            setTimeout(() => {
                const existing = latestExistingSet.current;
                grdWareResi.current?.selectRowsByCondition?.(row => existing.has(row.recd));
            }, 300);
        }
    }, []);

    const onSearch = async () => {
        pendingPreSelect.current = true;
        const para = `{"kVnt1":"${vendor}","recd":"","useyn":"Y"}`;
        setSearchApi(SERVER_URL + "/rst02/resi/search?query=" + encodeURI(para));
    };

    useEffect(() => {
        const init = async () => {
            await loadExistingResidents();
            onSearch();
        };
        if (wrcd) init();
    }, [wrcd]);

    const onConfirm = async () => {
        const selectedNodes = grdWareResi.current?.getSelectedNodes?.() || [];
        const selectedSet = new Set(selectedNodes.map(node => node.data?.recd).filter(Boolean));
        const existingSet = latestExistingSet.current;
        const allRows = grdWareResi.current?.getRowAllData?.() || [];

        const toInsert = selectedNodes
            .filter(node => !existingSet.has(node.data?.recd))
            .map(node => ({ vand: vendor, wrcd, recd: node.data.recd }));
        const toDelete = allRows
            .filter(row => existingSet.has(row.recd) && !selectedSet.has(row.recd))
            .map(row => ({ vand: vendor, wrcd, recd: row.recd }));

        if (toInsert.length === 0 && toDelete.length === 0) {
            setMessage({ openAlert: true, severity: "info", msg: "변경된 항목이 없습니다." });
            return;
        }

        const errors = [];
        if (toInsert.length > 0) {
            try {
                const data = await fetch(SERVER_URL + "/rst01/wareresi/insert", {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: token },
                    body: JSON.stringify(toInsert),
                }).then(r => r.json());
                if (!data.success) errors.push(responseMessage(data, "상주원 연계 등록에 실패했습니다."));
            } catch (e) {
                errors.push("상주원 연계 등록 서버 오류");
            }
        }
        if (toDelete.length > 0 && errors.length === 0) {
            try {
                const data = await fetch(SERVER_URL + "/rst01/wareresi/delete", {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: token },
                    body: JSON.stringify(toDelete),
                }).then(r => r.json());
                if (!data.success) errors.push(responseMessage(data, "상주원 연계 해제에 실패했습니다."));
            } catch (e) {
                errors.push("상주원 연계 해제 서버 오류");
            }
        }

        if (errors.length > 0) {
            setMessage({ openAlert: true, severity: "error", msg: errors.join(" / ") });
            return;
        }

        setMessage({ openAlert: true, severity: "success", msg: `완료: 연계 ${toInsert.length}건, 해제 ${toDelete.length}건` });
        onClose(true);
    };

    const formatPhone = value => {
        const digits = String(value ?? "").replace(/\D/g, "");
        if (digits.length === 11) return digits.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
        if (digits.length === 10) {
            return digits.startsWith("02")
                ? digits.replace(/(\d{2})(\d{4})(\d{4})/, "$1-$2-$3")
                : digits.replace(/(\d{3})(\d{3,4})(\d{4})/, "$1-$2-$3");
        }
        if (digits.length === 9 && digits.startsWith("02")) return digits.replace(/(\d{2})(\d{3})(\d{4})/, "$1-$2-$3");
        return value ?? "";
    };

    const columnDefs = [
        { headerName: "상주원코드", field: "recd", sortable: true, filter: true, flex: 1, minWidth: 120, editable: false, cellClass: ["nocheckbox", "alignC"], checkboxSelection: true, headerCheckboxSelection: true },
        { headerName: "상주원명", field: "renm", sortable: true, filter: true, flex: 1, minWidth: 140, editable: false, cellClass: ["nocheckbox", "alignL"] },
        { headerName: "연락처", field: "reph", sortable: true, filter: true, flex: 1, minWidth: 130, editable: false, cellClass: ["nocheckbox", "alignC"] , valueFormatter: params => formatPhone(params.value),},
        //  {
        //     headerName: "연락처", field: "reph", sortable: true, filter: true, flex: 1, minWidth: 120,
        //     editable: false, headerClass: "", cellClass: ["nocheckbox", "alignC"],
        //     singleClickEdit: false, hide: false,
        //     valueFormatter: params => formatPhone(params.value),
        // },
    ];

    return (
        <>
            <CustomAlert open={openAlert} severity={severity} duration={3000} message={msg}
                onClose={() => setMessage({ openAlert: false, severity: "", msg: "" })}
                anchorOrigin={{ vertical: "top", horizontal: "center" }} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                <b style={{ color: "#cd5c5c", fontFamily: "PretendardLight, NanumGothic, sans-serif", fontSize: "14px" }}>
                    창고별 상주원 연계 - [{wrcd}]
                </b>
                <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                    <button
                        onClick={() => onClose(false)}
                        style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0 }}
                    >
                        <img src="/images/button/close_button.png" alt="close" />
                    </button>
                </div>
            </div>
            <div style={{
                display: "flex", flexFlow: "row nowrap", justifyContent: "flex-end",
                alignItems: "center", height: "50px",
                borderTop: "1px solid #BFBDCD", borderBottom: "1px solid #BFBDCD",
                background: "#fff", paddingRight: "5px",
            }}>
                <button
                    type="button" onClick={onSearch}
                    style={{
                        display: "flex", alignItems: "center", height: "30px",
                        border: "1px solid #B7BCD6", borderRadius: "2px", background: "#fff",
                        marginLeft: "10px", fontWeight: 600, cursor: "pointer",
                        padding: "0 10px 0 8px", fontSize: "13px",
                        fontFamily: "PretendardLight, NanumGothic, sans-serif",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "#E8ECF5"}
                    onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                >
                    <img src="/images/button/btn_search.png" alt="Search" style={{ marginRight: "6px", marginLeft: "2px" }} />
                    <span>조회</span>
                </button>
                <button
                    type="button" onClick={onConfirm}
                    style={{
                        display: "flex", alignItems: "center", height: "30px",
                        border: "1px solid #B7BCD6", borderRadius: "2px", background: "#fff",
                        marginLeft: "10px", fontWeight: 600, cursor: "pointer",
                        padding: "0 10px 0 8px", fontSize: "13px",
                        fontFamily: "PretendardLight, NanumGothic, sans-serif",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "#E8ECF5"}
                    onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                >
                    <img src="/images/button/btn_insert.png" alt="insert" style={{ marginRight: "6px", marginLeft: "2px" }} />
                    <span>입력</span>
                </button>
            </div>
            <div style={{ background: "#e3f2fd", border: "1px solid #90caf9", borderRadius: "4px", padding: "5px 10px", margin: "4px 0", fontSize: "12px", color: "#1565c0" }}>
                {/* 이미 연계된 상주원은 체크되어 표시됩니다. 체크 변경 후 입력을 눌러 저장하세요. */}
                📣 이미 연계된 상주원은 <b>체크됨</b>으로 표시됩니다. 체크 변경 후 <b>✔ 입력</b>을 클릭하세요.
            </div>
            <div className="grid_type01">
                <div className="grid_wrap01">
                    <div className="grid_cont_box" style={{ height: "360px", width: "100%" }}>
                        <Grid ref={grdWareResi} gridId="WARERESI" columnDefs={columnDefs}
                            pagination={false} paginationCsv={false} paginationPageSize="1000"
                            rowMultiSelectWithClick={false} suppressRowClickSelection={true}
                            rowSelection="multiple" enableRangeSelection={false} enableCellTextSelection={true}
                            isFirst={true} displayNo={false} searchApi={searchApi} onClearApi={onClearApi}
                            onCellValueChangedEvent={async () => {}} useMessage={false} />
                    </div>
                </div>
            </div>
        </>
    );
}

export default WareResi;
